import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock, Inbox, AlertCircle, ArrowRight, Pencil, Trash2, Check, X } from "lucide-react";

/**
 * "Captured" — the caller's PRIVATE signals (`signals.forWeek`), each editable /
 * deletable (owner-scoped `signals.update` / `signals.delete`). Defaults to the
 * SELECTED day (the destination day you're capturing into) with a toggle to the
 * whole week. Capture is capture-only: turning captures into a receipt happens in
 * the gated Drafts tab, reached via "Review in Drafts". Nothing here shares.
 */
type Signals = RouterOutputs["signals"]["forWeek"];
type Signal = Signals[number];

function timeLabel(occurredAt: string | Date, isAr?: boolean): string {
  const d = typeof occurredAt === "string" ? new Date(occurredAt) : occurredAt;
  return d.toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", { weekday: "short", month: "short", day: "numeric" });
}
function dayKeyOf(occurredAt: string | Date): string {
  const d = typeof occurredAt === "string" ? new Date(occurredAt) : occurredAt;
  return d.toISOString().slice(0, 10);
}

export function CapturedReview({
  weekStartKey,
  teamId,
  selectedDay,
  onReviewInDrafts,
}: {
  weekStartKey: string;
  teamId: number | null;
  /** The destination day currently selected in the Capture sheet (YYYY-MM-DD). */
  selectedDay: string;
  /** Close the Capture sheet and go to the Drafts tab. */
  onReviewInDrafts?: () => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const enabled = teamId != null;
  const signals = trpc.signals.forWeek.useQuery(
    { weekStartKey, workspaceTeamId: teamId },
    { enabled },
  );

  const utils = trpc.useUtils();
  const invalidate = () => {
    utils.signals.forWeek.invalidate();
    utils.receipts.weekStatus.invalidate();
    utils.receipts.forWeek.invalidate();
    utils.yourWeek.forWeek.invalidate();
  };
  const updateSignal = trpc.signals.update.useMutation({ onSuccess: invalidate });
  const deleteSignal = trpc.signals.delete.useMutation({ onSuccess: invalidate });

  const [view, setView] = useState<"day" | "week">("day");
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  const dayLabel = new Date(selectedDay + "T00:00:00.000Z").toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  const all = (signals.data as any[]) ?? [];
  const list = view === "day" ? all.filter(s => dayKeyOf(s.occurredAt) === selectedDay) : all;

  const startEdit = (s: any) => {
    setEditId(s.id);
    setEditText(s.title ?? "");
  };
  const saveEdit = () => {
    const text = editText.trim();
    if (editId == null || !text) return setEditId(null);
    updateSignal.mutate({ signalId: editId, title: text.slice(0, 280) });
    setEditId(null);
  };

  return (
    <section className="mt-1" data-testid="captured-review">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold tracking-wide text-muted-foreground">
          {view === "day"
            ? isAr ? `المُسجَّل · ${dayLabel}` : `CAPTURED · ${dayLabel}`
            : isAr ? "المُسجَّل هذا الأسبوع" : "CAPTURED THIS WEEK"}
          {!signals.isLoading && !signals.isError ? ` · ${list.length}` : ""}
        </div>
        <button
          onClick={() => setView(v => (v === "day" ? "week" : "day"))}
          className="text-[11px] font-medium text-coral hover:underline"
          data-testid="captured-view-toggle"
        >
          {view === "day"
            ? isAr ? "عرض كامل الأسبوع" : "View all week"
            : isAr ? "هذا اليوم فقط" : "This day only"}
        </button>
      </div>

      {signals.isLoading ? (
        <div className="space-y-2" data-testid="captured-loading">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : signals.isError ? (
        <div className="rounded-lg border border-border p-3" data-testid="captured-error">
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-coral" /> {isAr ? "تعذّر تحميل تسجيلاتك." : "Couldn’t load your captures."}
          </div>
          <Button onClick={() => signals.refetch()} size="sm" variant="outline" className="mt-2 rounded-full">
            {isAr ? "إعادة المحاولة" : "Retry"}
          </Button>
        </div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center text-center gap-1 py-4 text-muted-foreground" data-testid="captured-empty">
          <Inbox className="h-6 w-6" />
          <div className="text-sm">
            {view === "day"
              ? isAr
                ? `لا شيء مُسجَّل ليوم ${dayLabel} بعد. أضف ملاحظة أو تسجيلاً صوتياً أو صورة.`
                : `Nothing captured for ${dayLabel} yet. Add a note, voice memo or photo.`
              : isAr
                ? "لا شيء مُسجَّل هذا الأسبوع بعد. أضف ملاحظة أو تسجيلاً صوتياً أو صورة."
                : "Nothing captured yet this week. Add a note, voice memo or photo."}
          </div>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border" data-testid="captured-list">
            {list.slice(0, 12).map((s: Signal) => {
              const id = (s as any).id as number;
              const editing = editId === id;
              return (
                <li key={id} className="flex items-center gap-2 px-3 py-2">
                  {editing ? (
                    <>
                      <input
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveEdit();
                          if (e.key === "Escape") setEditId(null);
                        }}
                        autoFocus
                        className="min-w-0 flex-1 rounded-md border border-coral bg-background px-2 py-1 text-sm outline-none"
                      />
                      <button aria-label={isAr ? "حفظ" : "Save"} onClick={saveEdit} className="shrink-0 rounded-md p-1 text-emerald-600 hover:bg-muted">
                        <Check className="h-4 w-4" />
                      </button>
                      <button aria-label={isAr ? "إلغاء" : "Cancel"} onClick={() => setEditId(null)} className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1 truncate text-sm">{(s as any).title}</span>
                      {view === "week" ? (
                        <span className="shrink-0 text-[11px] text-muted-foreground">{timeLabel((s as any).occurredAt, isAr)}</span>
                      ) : null}
                      <button aria-label={isAr ? "تعديل" : "Edit"} onClick={() => startEdit(s)} className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        aria-label={isAr ? "حذف" : "Delete"}
                        onClick={() => deleteSignal.mutate({ signalId: id })}
                        disabled={deleteSignal.isPending}
                        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          {onReviewInDrafts ? (
            <Button
              onClick={onReviewInDrafts}
              variant="outline"
              size="sm"
              className="mt-3 w-full rounded-full"
              data-testid="captured-review-in-drafts"
            >
              {isAr ? "راجع واعتمد في المسودات" : "Review & approve in Drafts"} <ArrowRight className="h-3.5 w-3.5 ms-1" />
            </Button>
          ) : null}
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" /> {isAr ? "خاص بك. لن تتم مشاركة أي شيء حتى تعتمده في المسودات." : "Private to you. Nothing shares until you approve in Drafts."}
          </p>
        </>
      )}
    </section>
  );
}
